import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import { initThemeEarly } from "./hooks/useTheme";
import { installConsoleCapture } from "./lib/debugCapture";
import { installNetworkCapture, installSessionReplay } from "./lib/pageCapture";

// Apply stored theme BEFORE React mounts to prevent a dark→light flash.
initThemeEarly();
// Start buffering console.error/warn + unhandled rejections so bug reports
// can auto-attach the last 10 errors as repro context.
installConsoleCapture();
// Ring-buffer recent fetch calls and a 30s session replay so the auto bug-report
// dialog can attach them when an uncaught error fires.
installNetworkCapture();
installSessionReplay();

// Dev-only canonical-host drift warning. Catches situations where the wrong
// domain alias is set as primary (e.g. hackerlab.ai vs hackerlabs.ai), or a
// new alias is added without updating canonical config. Production stays
// silent — the runtime canonical updater + Cloudflare 301 handle real redirects.
if (import.meta.env.DEV) {
  const CANONICAL_HOST = "hackerlabs.ai";
  const ALLOWED_HOSTS = new Set([
    CANONICAL_HOST,
    "localhost",
    "127.0.0.1",
    "0.0.0.0",
  ]);
  const host = window.location.hostname;
  const isAllowed =
    ALLOWED_HOSTS.has(host) ||
    host.endsWith(".lovable.app") ||
    host.endsWith(".lovableproject.com");
  if (!isAllowed) {
    // eslint-disable-next-line no-console
    console.warn(
      `[canonical-host] Running on "${host}" but canonical is "${CANONICAL_HOST}". ` +
        `If this is unexpected, check Project Settings → Domains and confirm "${CANONICAL_HOST}" is set as primary.`,
    );
  }
}

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <App />
  </HelmetProvider>
);
